import React, { useMemo, useState, useCallback } from 'react';
import { Menu, Dropdown, Button, message, Select } from 'antd';
import { CopyOutlined, DownOutlined, LinkOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Document } from '../../types/documentTypes';
import { BUYER_ROLES, SUPPLIER_ROLES, BUYER_ADMIN, SUPPLIER_ADMIN } from '../roles/roles';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { stringArrToList, userRoleTextFormatter } from '../../utils/DataFormatterUtils';
import { getRolePermissionKeyMap } from '../../utils/AuthUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import {
	getCompanyWideDocumentPermissions,
	getDocumentPermissionsByUserType,
	getMergedDocumentPermissions,
	mergeDocumentPermissions,
	normalizePermissionsForAllRoles,
	canRoleAccessDocumentFromPermissions,
	canRoleModifyDocumentFromPermissions,
	canUserDeleteDocument,
} from '../../utils/LocationUtils';

interface DocumentPermissionsDropdownProps {
	fullDocument?: Document;
	userType: string;
	companyConfig: any;
	updateDocument: (data: Document) => Promise<void>;
	currentUser: any;
	editable?: boolean;
	handleCopyLink: () => void;
}

type PermissionLevel = 'no-access' | 'can-view' | 'can-edit';

const DocumentPermissionsDropdown: React.FC<DocumentPermissionsDropdownProps> = ({
	fullDocument,
	userType,
	companyConfig,
	updateDocument,
	currentUser,
	editable = true,
	handleCopyLink,
}) => {
	const [updatingRole, setUpdatingRole] = useState<string | null>(null);

	const canEditPermissions = useMemo(() => {
		return canUserDeleteDocument(fullDocument, currentUser);
	}, [fullDocument, currentUser]);

	const basePermissions = useMemo(() => {
		if (!fullDocument) return null;
		return getDocumentPermissionsByUserType(fullDocument, userType);
	}, [fullDocument, userType]);

	const companyWidePermissions = useMemo(() => {
		return getCompanyWideDocumentPermissions(userType, companyConfig);
	}, [userType, companyConfig]);

	const mergedPermissions = useMemo(() => {
		if (!fullDocument) return null;
		const existingMerged = nullSafeGet('mergedPermissions', fullDocument);
		if (existingMerged) {
			return existingMerged;
		}
		return getMergedDocumentPermissions(fullDocument, userType, companyConfig);
	}, [fullDocument, userType, companyConfig]);

	const rolePermissionMap = useMemo(() => getRolePermissionKeyMap(userType), [userType]);

	const availableRoles = useMemo(() => {
		const allRoles = userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ROLES : BUYER_ROLES;
		return allRoles.filter((role) => role !== BUYER_ADMIN && role !== SUPPLIER_ADMIN);
	}, [userType]);

	const canRoleEdit = useMemo(() => {
		if (!mergedPermissions) return () => false;
		return (role: string) => {
			if (role === BUYER_ADMIN || role === SUPPLIER_ADMIN) return true;
			return canRoleModifyDocumentFromPermissions(mergedPermissions, userType, role);
		};
	}, [mergedPermissions, userType]);

	const canRoleView = useMemo(() => {
		if (!mergedPermissions) return () => false;
		return (role: string) => {
			if (role === BUYER_ADMIN || role === SUPPLIER_ADMIN) return true;
			return canRoleAccessDocumentFromPermissions(mergedPermissions, userType, role);
		};
	}, [mergedPermissions, userType]);

	const getCurrentPermissionLevel = useCallback(
		(role: string): PermissionLevel => {
			if (canRoleEdit(role)) {
				return 'can-edit';
			}
			if (canRoleView(role)) {
				return 'can-view';
			}
			return 'no-access';
		},
		[canRoleEdit, canRoleView]
	);

	const handlePermissionChange = useCallback(
		async (role: string, newLevel: PermissionLevel) => {
			if (!fullDocument || updatingRole) return;

			setUpdatingRole(role);

			const permissionKey = rolePermissionMap[role];
			if (!permissionKey) {
				setUpdatingRole(null);
				return;
			}

			const currentPermissions = basePermissions
				? mergeDocumentPermissions(basePermissions, companyWidePermissions)
				: companyWidePermissions;

			const normalizedPermissions = normalizePermissionsForAllRoles(userType, currentPermissions);

			if (normalizedPermissions.access && normalizedPermissions.modify) {
				normalizedPermissions.access[permissionKey] =
					newLevel === 'can-view' || newLevel === 'can-edit';
				normalizedPermissions.modify[permissionKey] = newLevel === 'can-edit';
			}

			const permissionsFieldName =
				userType === ROLE_TYPES.BUYER ? 'buyerPermissions' : 'supplierPermissions';

			const documentData: Document = {
				...fullDocument,
				[permissionsFieldName]: normalizedPermissions,
			};

			try {
				await updateDocument(documentData);
				message.success('Permissions updated successfully');
			} catch (err) {
				console.error('Error saving permissions:', err);
				message.error('Failed to save permissions');
			} finally {
				setUpdatingRole(null);
			}
		},
		[
			fullDocument,
			updatingRole,
			rolePermissionMap,
			basePermissions,
			companyWidePermissions,
			userType,
			updateDocument,
		]
	);

	const allRoles = useMemo(() => {
		return userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ROLES : BUYER_ROLES;
	}, [userType]);

	const renderMenu = useCallback(() => {
		let menuItems = null;
		if (!canEditPermissions) {
			menuItems = allRoles.map((role) => {
				let canEdit = false;
				let canView = false;
				if (mergedPermissions) {
					if (role === BUYER_ADMIN || role === SUPPLIER_ADMIN) {
						canEdit = true;
						canView = true;
					} else {
						canEdit = canRoleModifyDocumentFromPermissions(mergedPermissions, userType, role);
						canView = canRoleAccessDocumentFromPermissions(mergedPermissions, userType, role);
					}
				}
				return (
					<Menu.Item key={role}>
						<div className="flex flex-row items-center justify-between">
							<span>{userRoleTextFormatter(role)}</span>
							<span className="text-sm text-gray-500">
								{canEdit ? 'Can Edit' : canView ? 'Can View' : 'No Access'}
							</span>
						</div>
					</Menu.Item>
				);
			});
		} else {
			menuItems = availableRoles.map((role) => {
				const currentLevel = getCurrentPermissionLevel(role);
				const isUpdating = updatingRole === role;

				return (
					<Menu.Item key={role} disabled={isUpdating}>
						<div className="flex w-full flex-row items-center justify-between gap-6 ">
							<span>{userRoleTextFormatter(role)}</span>
							<Select
								value={currentLevel}
								onChange={(value: PermissionLevel) => handlePermissionChange(role, value)}
								disabled={isUpdating}
								size="small"
								style={{ minWidth: 120, textAlign: 'right' }}
								onClick={(e) => e.stopPropagation()}
								bordered={false}
							>
								<Select.Option value="no-access">No Access</Select.Option>
								<Select.Option value="can-view">Can View</Select.Option>
								<Select.Option value="can-edit">Can Edit</Select.Option>
							</Select>
						</div>
					</Menu.Item>
				);
			});
		}

		return (
			<Menu>
				<Menu.Item key="divider">
					<div className="flex flex-row items-center justify-between">
						<span className="bodymedium">Share this document</span>
						<Button type="link" onClick={handleCopyLink}>
							copy link <CopyOutlined />
						</Button>
					</div>
				</Menu.Item>
				<Menu.Divider />
				{menuItems}
			</Menu>
		);
	}, [
		canEditPermissions,
		allRoles,
		mergedPermissions,
		userType,
		availableRoles,
		getCurrentPermissionLevel,
		updatingRole,
		handlePermissionChange,
		handleCopyLink,
	]);

	return (
		<Dropdown overlay={renderMenu()} placement="bottomRight" trigger={['click']}>
			<Button icon={<ShareAltOutlined />}>
				Share <DownOutlined />
			</Button>
		</Dropdown>
	);
};

export default DocumentPermissionsDropdown;
